/**
 * Step 2: EXPRESS-only ETA (minutes) from user ↔ vendor lat/lng via haversine.
 * No UI, no scheduling, no external APIs. Debug logs only.
 * Depends on SkippDeliveryClassification (Step 1).
 */
(function (global) {
  const BRAND_JSON_SEL = '[data-brand-schedules-json]';
  const MIN_ETA_MINUTES = 15;
  /** Added on top of distance-derived travel minutes (vendor ↔ user). */
  const ETA_BUFFER_MINUTES = 5;
  const MINUTES_PER_KM = 3;
  const R_EARTH_KM = 6371;

  let __skippLastBrandScheduleLogSig = '';

  const FALLBACK_EXPRESS_MINUTES =
    typeof global.__SKIPP_DEFAULT_EXPRESS_ETA_MINUTES__ === 'number' &&
    Number.isFinite(global.__SKIPP_DEFAULT_EXPRESS_ETA_MINUTES__)
      ? Math.max(MIN_ETA_MINUTES, Math.round(global.__SKIPP_DEFAULT_EXPRESS_ETA_MINUTES__))
      : 45;

  function calculateDistanceKm(lat1, lon1, lat2, lon2) {
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R_EARTH_KM * c;
  }

  function getUserCoords() {
    if (typeof global.userLat === 'number' && typeof global.userLng === 'number') {
      if (Number.isFinite(global.userLat) && Number.isFinite(global.userLng)) {
        return { lat: global.userLat, lng: global.userLng };
      }
    }
    try {
      const legacy = localStorage.getItem('userLocation');
      if (legacy) {
        const j = JSON.parse(legacy);
        const lat = j.coordinates?.latitude ?? j.lat;
        const lng = j.coordinates?.longitude ?? j.lng;
        if (typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    } catch {
      /* ignore */
    }
    const mvc = global.multiVendorCart?.userLocation?.coordinates;
    if (
      mvc &&
      typeof mvc.latitude === 'number' &&
      typeof mvc.longitude === 'number' &&
      Number.isFinite(mvc.latitude) &&
      Number.isFinite(mvc.longitude)
    ) {
      return { lat: mvc.latitude, lng: mvc.longitude };
    }
    return null;
  }

  function parseBrandSchedules() {
    if (typeof document === 'undefined') return [];
    const elements = document.querySelectorAll(BRAND_JSON_SEL);
    let raw = '';
    for (let i = 0; i < elements.length; i += 1) {
      const t = (elements[i].textContent || '').trim();
      if (t && t !== '[]') {
        raw = elements[i].textContent || '[]';
        break;
      }
    }
    if (!raw) raw = '[]';
    try {
      const parsed = JSON.parse(raw);
      const schedules = Array.isArray(parsed) ? parsed : [];

      if (typeof global.metaobjects !== 'object' || global.metaobjects == null) {
        global.metaobjects = {};
      }
      if (typeof global.metaobjects.brand_schedule !== 'object' || global.metaobjects.brand_schedule == null) {
        global.metaobjects.brand_schedule = {};
      }
      global.metaobjects.brand_schedule.values = schedules;

      const sig = JSON.stringify(schedules);
      if (sig !== __skippLastBrandScheduleLogSig) {
        __skippLastBrandScheduleLogSig = sig;
        console.log('METAOBJECT RAW:', global.metaobjects);
        const fromValues = global.metaobjects?.brand_schedule?.values || [];
        console.log('SCHEDULES:', fromValues);
        fromValues.forEach((schedule) => {
          console.log('SINGLE SCHEDULE:', {
            brand: schedule.brand_name,
            open: schedule.open_time,
            close: schedule.close_time,
            offDays: schedule.off_days,
            closed: schedule.is_closed_today,
          });
        });
      }

      return schedules;
    } catch (err) {
      console.warn('parseBrandSchedules JSON error:', err, raw.slice(0, 200));
      return [];
    }
  }

  function validatedLatLng(lat, lng) {
    if (lat == null || lng == null) return null;
    const la = Number(lat);
    const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
    return { lat: la, lng: ln };
  }

  /**
   * schedule.brand_name ↔ item.vendor via SkippDeliveryClassification.normalize
   */
  function findScheduleForVendor(schedules, vendor) {
    const n = global.SkippDeliveryClassification?.normalize;
    const normalizeFn = typeof n === 'function' ? n : (str) => (str || '').trim().toLowerCase();
    const key = normalizeFn(vendor);
    if (!key) return null;
    return schedules.find((s) => normalizeFn(s?.brand_name) === key) || null;
  }

  function extractVendorCoords(schedule) {
    return validatedLatLng(schedule?.latitude, schedule?.longitude);
  }

  function minutesFromDistanceKm(km) {
    return Math.ceil(Math.max(0, Number(km) || 0) * MINUTES_PER_KM);
  }

  /** UX label: min 15; 15–60 exact; >60 bands. */
  function formatEtaDisplay(minutes) {
    const m = Math.max(MIN_ETA_MINUTES, Math.ceil(Number(minutes) || 0));
    if (m <= 60) return `${m} mins`;
    if (m <= 90) return '60–90 mins';
    return '90+ mins';
  }

  /**
   * Raw minutes from geometry or fallback; then apply 15m floor for stored etaMinutes.
   */
  function computeExpressEtaMinutes(item, schedules, userCoords) {
    const schedule = findScheduleForVendor(schedules, item.vendor);
    const vendorCoords = schedule ? extractVendorCoords(schedule) : null;

    let rawMinutes;
    if (userCoords && vendorCoords) {
      const km = calculateDistanceKm(userCoords.lat, userCoords.lng, vendorCoords.lat, vendorCoords.lng);
      rawMinutes = minutesFromDistanceKm(km) + ETA_BUFFER_MINUTES;
    } else {
      rawMinutes = FALLBACK_EXPRESS_MINUTES;
    }

    return Math.max(MIN_ETA_MINUTES, rawMinutes);
  }

  /**
   * @param {object[]} cartAnalysis from Step 1
   * @param {{ items?: object[] }} cart
   * @param {object[]} schedules from brand_schedule JSON
      * @param {{ lat: number, lng: number } | null} userCoords
   */
  function enrichCartAnalysisWithEta(cartAnalysis, cart, schedules, userCoords) {
    const D = global.SkippDeliveryClassification;
    const EXPRESS = D?.EXPRESS || 'EXPRESS';

    const itemsByKey = {};
    (cart?.items || []).forEach((it) => {
      if (it.key) itemsByKey[it.key] = it;
    });

    const cartWithETA = (cartAnalysis || []).map((row) => {
      if (row.deliveryType !== EXPRESS) {
        return { ...row, eta: null, etaMinutes: null, etaDisplay: null };
      }

      const lineItem = row.key ? itemsByKey[row.key] : null;
      const itemForEta = lineItem || { vendor: row.vendor, key: row.key, id: row.id };

      const etaMinutes = computeExpressEtaMinutes(itemForEta, schedules, userCoords);

      return {
        ...row,
        eta: etaMinutes,
        etaMinutes,
        etaDisplay: formatEtaDisplay(etaMinutes),
      };
    });

    const expressEtas = cartWithETA.filter((i) => i.deliveryType === EXPRESS && i.etaMinutes != null).map((i) => i.etaMinutes);
    const cartETA = expressEtas.length ? Math.max(...expressEtas) : null;
    const cartETADisplay = cartETA != null ? formatEtaDisplay(cartETA) : null;

    console.log('ETA per item:', cartWithETA);
    console.log('Cart ETA:', cartETA, cartETADisplay);

    return { cartWithETA, cartETA, cartETADisplay };
  }

  async function runEtaPipelineFromFetch() {
    const D = global.SkippDeliveryClassification;
    if (!D || typeof D.runClassificationFromFetch !== 'function') return null;

    const userCity = D.resolveUserCityString();
    const userCoords = getUserCoords();
    const schedules = parseBrandSchedules();

    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const cart = await res.json();
      const { cartAnalysis } = D.analyzeCart(cart, userCity, { silent: true });
      return enrichCartAnalysisWithEta(cartAnalysis, cart, schedules, userCoords);
    } catch {
      return null;
    }
  }

  function runEtaPipelineFromCartPayload(cart) {
    const D = global.SkippDeliveryClassification;
    if (!D || !cart || !Array.isArray(cart.items)) return null;

    const userCity = D.resolveUserCityString();
    const userCoords = getUserCoords();
    const schedules = parseBrandSchedules();
    const { cartAnalysis } = D.analyzeCart(cart, userCity, { silent: true });
    return enrichCartAnalysisWithEta(cartAnalysis, cart, schedules, userCoords);
  }

  global.SkippDeliveryEtaCalculation = {
    MIN_ETA_MINUTES,
    ETA_BUFFER_MINUTES,
    FALLBACK_EXPRESS_MINUTES,
    calculateDistanceKm,
    getUserCoords,
    parseBrandSchedules,
    findScheduleForVendor,
    formatEtaDisplay,
    computeExpressEtaMinutes,
    enrichCartAnalysisWithEta,
    runEtaPipelineFromFetch,
    runEtaPipelineFromCartPayload,
  };
})(typeof window !== 'undefined' ? window : globalThis);
